import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Heading, Stack, Text } from '@eneco/sparky';

import type { PersonalizedVideoContent } from './personalized-video-content.types';

/**
 * `PersonalizedVideo` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/PersonalizedVideo/
 * PersonalizedVideo.tsx`): an optional title, a `PingVP` video-embed marker div the third-party
 * script targets, and an optional description below it.
 *
 * `data-language="Dutch"` is hardcoded in the legacy component regardless of locale — carried over
 * unchanged rather than "fixed", since that's a pre-existing quirk outside this migration's scope.
 */
const PersonalizedVideo: FC = () => {
  const { adapter } = useCms();
  const content = useContent<PersonalizedVideoContent>();

  const description = adapter.toRichText(content.description);

  return (
    <Stack gap="8" direction="column">
      {content.title && (
        <Heading size="M" as="h3">
          {content.title}
        </Heading>
      )}
      <div
        className="PingVP"
        data-video=""
        data-language="Dutch"
        data-controls="below"
        data-autostart="false"
        data-testid="personalized-video-embed"
      />
      {description && (
        <Text size={{ sm: 'BodyL' }}>
          <RichText html={description} />
        </Text>
      )}
    </Stack>
  );
};

export default PersonalizedVideo;
